import { Injectable } from '@angular/core';
import { DataServerService } from '../api/data-server.service';
import { StorageService } from '../data/storage.service';
import { SubscriberService } from './subscriber.service';
import { AlertService } from '../base/alert.service';
import { LoginService } from '../auth/login.service';
import { OfflineService } from './offline.service';
import { Order } from 'src/app/interfaces/orders';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  constructor(
    private dataServer: DataServerService,
    private storage: StorageService,
    private sub: SubscriberService,
    private alert: AlertService,
    private loginService: LoginService,
    private offline: OfflineService
  ) {}

  // Products
  async loadProducts() {
    const productList = await this.storage.getData('productList');
    if (productList) {
      this.sub.productListNext(productList);
      this.refreshProducts();
    } else {
      this.refreshProducts();
    }
  }

  async refreshProducts() {
    const response = await this.dataServer.getProducts();
    if (response.status === 200) {
      let productData = response.data.docs;

      productData.sort((a: any, b: any) => {
        if (a.model < b.model) {
          return -1;
        } else if (a.model > b.model) {
          return 1;
        } else {
          return 0;
        }
      });

      this.storage.setData('productList', productData);
      this.sub.productListNext(productData);
    }
  }

  // Contacts
  async loadGoogleContacts() {
    // Check if user is admin
    await this.loginService.checkLoginStatus();
    const userRole = this.loginService.decodedToken?.role;

    // Only proceed if user is admin
    if (userRole !== 'admin') {
      console.log('Google Contacts load skipped: User is not admin');
      return;
    }

    const googleContactList = await this.storage.getData('googleContactList');
    if (googleContactList) {
      this.sub.googleContactListNext(googleContactList);
      this.refreshGoogleContacts();
    } else {
      this.refreshGoogleContacts();
    }
  }

  async refreshGoogleContacts(showAlert: boolean = false) {
    try {
      // Check if user is admin
      await this.loginService.checkLoginStatus();
      const userRole = this.loginService.decodedToken?.role;

      // Only proceed if user is admin
      if (userRole !== 'admin') {
        return;
      }

      const response = await this.dataServer.getGoogleContacts();

      if (response.status === 200) {
        let contactData = response.data.docs ?? response.data.list ?? [];

        // Kontakte sortieren
        contactData.sort(
          (a: any, b: any) => a.displayName?.localeCompare(b.displayName) || 0
        );

        // Verarbeite die Daten
        this.storage.setData('googleContactList', contactData);
        this.sub.googleContactListNext(contactData);
      } else {
        console.error(
          'Fehler beim Abrufen der Kontakte:',
          response.status,
          response.data
        );

      }
    } catch (error) {
      console.error('Ein Fehler ist aufgetreten:', error);
    }
  }

  async loadLexwareContacts() {

    const contactList = await this.storage.getData('lexwareContactList');
    if (contactList) {
      this.sub.lexwareContactListNext(contactList);
      this.refreshLexwareContacts();
    } else {
      this.refreshLexwareContacts();
    }
  }

  async refreshLexwareContacts(showAlert: boolean = false) {
    try {
      const response = await this.dataServer.getLexwareContacts();

      if (response.status === 200) {
        let contactData = response.data.docs;

        // Kontakte sortieren
        contactData.sort(
          (a: any, b: any) => a.name?.localeCompare(b.companyName) || 0
        );

        // Verarbeite die Daten
        this.storage.setData('lexwareContactList', contactData);
        this.sub.lexwareContactListNext(contactData);
      } else {
        console.error(
          'Fehler beim Abrufen der Kontakte:',
          response.status,
          response.data
        );

        if(showAlert) {
          this.alert.presentInfoAlert(
            'Lexware Kontakte fehler',
            response.data
          )
        }
      }
    } catch (error) {
      console.error('Ein Fehler ist aufgetreten:', error);
    }
  }

  // Orders
  async loadOrders() {
    const dataList = await this.storage.getData('orderList');
    if (dataList) {
      this.sub.orderListNext(dataList);
      this.refreshOrders();
    } else {
      this.refreshOrders();
    }
  }

  async refreshOrders() {
    try {
      const response = await this.dataServer.getOrders();

      if (response.status === 200) {
        let dataList = response.data.docs;

        // Kontakte sortieren
        dataList.sort(
          (a: any, b: any) => a.updated?.localeCompare(b.updated) || 0
        );

        // Verarbeite die Daten
        this.storage.setData('orderList', dataList);
        this.sub.orderListNext(dataList);

        // Kein automatisches Löschen/Verschieben von offlineOrders mehr hier.
        // offlineOrders werden ausschließlich in OrderService.updateOrCreateOrder() nach
        // einem nachweislich erfolgreichen Upsert via offline.markOfflineOrderSynced()
        // verschoben — niemals aufgrund eines Zeitstempel-Vergleichs beim Refresh.
      } else {
        console.error(
          'Fehler beim Abrufen der Order:',
          response.status,
          response.data
        );
      }
    } catch (error) {
      console.error('Ein Fehler ist aufgetreten:', error);
    }
  }

  async handleOrderCreatedEvent(order: Order) {
    this.alert.presentToast(`Neuer Auftrag: ${order.orderNumber ?? order.orderId}`, 4000, 'success');
    await this.refreshOrders();
  }

  /**
   * Serverseitig bestätigte Änderung an einem Auftrag (Socket-Event `order:updated`).
   *
   * Bewusst ohne Toast: Änderungen sind der Normalfall und treten beim Bearbeiten
   * in schneller Folge auf — eine Meldung pro Speichervorgang wäre unbrauchbar.
   * Aktualisiert wird deshalb still die Auftragsliste.
   *
   * refreshOrders() rührt weder currentOrder noch offlineOrders an, ein gerade
   * offener Auftrag kann dadurch also nicht überschrieben werden.
   */
  private orderRefreshQueued = false;

  async handleOrderUpdatedEvent(order: Order) {
    // Eigenes Echo überspringen: die eigene Änderung liegt lokal bereits vor.
    // Als String vergleichen: die Token-ID ist numerisch typisiert, die ID aus
    // dem Server-Dokument kommt als String — ein strikter Vergleich träfe nie zu.
    const ownId = this.loginService.decodedToken?.id;
    const updatedBy = (order as any)?.updatedBy;
    const updatedById = typeof updatedBy === 'string' ? updatedBy : updatedBy?.id;
    if (ownId != null && updatedById != null && String(ownId) === String(updatedById)) {
      return;
    }

    // Mehrere Events in schneller Folge (z. B. ein Kollege tippt) zu einem
    // einzigen Refresh zusammenfassen, statt die API pro Event zu belasten.
    if (this.orderRefreshQueued) {
      return;
    }
    this.orderRefreshQueued = true;
    setTimeout(async () => {
      this.orderRefreshQueued = false;
      await this.refreshOrders();
    }, 1000);
  }

  // Global
  async loadAll() {
    this.loadProducts();
    this.loadGoogleContacts();
    // Lexware-Kontakte werden nicht beim Login geladen — nur bei Bedarf
    // (z.B. current-order.component.ts, wenn die Kontaktliste leer ist).
    this.loadOrders();
  }

  async refreshAll() {
    this.refreshProducts();
    this.refreshGoogleContacts();
    // Lexware contacts are not auto-refreshed here, called explicitly via loadLexwareContacts()
    this.refreshOrders();
  }

  async loadAndRefreshAll() {
    this.loadAll();
    this.refreshAll();
  }


}
